'use client';

import { c as _c } from "react/compiler-runtime";
import * as Sentry from '@sentry/nextjs';
import { useMutation } from '@tanstack/react-query';
import Image from 'next/image';
import { Suspense, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import Button from '@/components/V2/RectButton/RectButton';
import { SUPPORT_URL } from '@/config/company';
import ErrorsMessages from '@/enums/errors';
import { AmplitudeEvent, amplitude } from '@/lib/analytics/amplitude';
import Session from '@/lib/auth/session';
import { translate } from '@/lib/i18n/translate';
import { exchangeCodeForTokens, initiateAuthFlow } from '@/lib/sync/auth';
import { initiateLoginRedirect } from '@/lib/sync/login';
import * as MessageService from '@/services/message';
import styles from './login.module.css';
function LoginContent() {
  const $ = _c(24);
  const {
    handleSubmit
  } = useForm();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp,
      onError: _temp2,
      onSuccess: _temp3
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    mutate: redirectSyncLogin,
    isPending: isRedirecting
  } = useMutation(t0);
  let t1;
  if ($[1] !== redirectSyncLogin) {
    t1 = function onSubmit() {
      redirectSyncLogin();
    };
    $[1] = redirectSyncLogin;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const onSubmit = t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  useEffect(_temp4, t2);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className={styles.login__background_mobile}><Image src="/assets/img/login-avatar.png" alt="Avatar Syde" width={526} height={825} sizes="(max-width: 768px) 50vw, 400px" className={styles.login__background_avatar} /></div>;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== handleSubmit || $[6] !== onSubmit) {
    t4 = handleSubmit(onSubmit);
    $[5] = handleSubmit;
    $[6] = onSubmit;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Image src="/assets/img/login-welcome-logo.svg" width={160} height={72} alt="Syde logo" className={styles.login__logo} />;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = translate("PAGES.LOGIN.TAGLINE");
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <span className={styles.login__subtitle_highlight}>{translate("PAGES.LOGIN.TAGLINE_HIGHLIGHT")}</span>;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <p className={styles.login__subtitle}>{t6}{" "}{t7}{" "}{translate("PAGES.LOGIN.TAGLINE_END")}</p>;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = translate("PAGES.LOGIN.SUBMIT_BUTTON");
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== isRedirecting) {
    t10 = <Button fullWidth={true} isLoading={isRedirecting} data-testid="submit-button" variant="primary" type="submit" disabled={isRedirecting} className={styles.login__submit_button}>{t9}</Button>;
    $[13] = isRedirecting;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = translate("PAGES.LOGIN.FOOTER_CLICK");
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <strong>{translate("PAGES.LOGIN.FOOTER_CREATE")}</strong>;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = translate("PAGES.LOGIN.FOOTER_OR");
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <strong>{translate("PAGES.LOGIN.FOOTER_ACCESS")}</strong>;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  let t15;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <p className={styles.login__footer}>{t11}{" "}{t12}{" "}{t13}{" "}{t14}{" "}{translate("PAGES.LOGIN.FOOTER_ACCOUNT")}</p>;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  let t16;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <a href={SUPPORT_URL} target="_blank" rel="noopener noreferrer" className={styles.login__link} data-testid="support-casa-link">{translate("PAGES.LOGIN.FAQ")}</a>;
    $[20] = t16;
  } else {
    t16 = $[20];
  }
  let t17;
  if ($[21] !== t10 || $[22] !== t4) {
    t17 = <div className={styles.login__container}>{t3}<form onSubmit={t4}>{t5}{t8}{t10}{t15}{t16}</form></div>;
    $[21] = t10;
    $[22] = t4;
    $[23] = t17;
  } else {
    t17 = $[23];
  }
  return t17;
}
function _temp4() {
  amplitude.logEvent(AmplitudeEvent.AUTH_OPENED);
}
async function _temp3(loginUrl_0) {
  window.location.href = loginUrl_0;
}
function _temp2(error) {
  Sentry.captureException(error, {
    tags: {
      method: "redirectSyncLogin"
    },
    extra: {
      error: error.message
    }
  });
  MessageService.error(ErrorsMessages.AUTHENTICATION_ERROR);
}
async function _temp() {
  await Session.clearToken();
  const code = await initiateAuthFlow();
  await exchangeCodeForTokens(code, "subscriber");
  const loginUrl = await initiateLoginRedirect();
  return loginUrl;
}
export default function Login() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Suspense><LoginContent /></Suspense>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}